import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useSupplierPriceList,
  useIngestSupplierPriceList,
} from '@/api/supplier-price-lists/supplier-price-lists.hooks';
import { DsButton } from '@/modules/internal-v2/ds';
import { IfPermitted } from '../../shared/if-permitted.component';
import { PERMISSIONS } from '@/constants/permissions';

type DetailTab = 'overview' | 'runs' | 'items';

const TABS: { id: DetailTab; label: string; suffix: string }[] = [
  { id: 'overview', label: 'Übersicht', suffix: '' },
  { id: 'runs', label: 'Läufe', suffix: '/runs' },
  { id: 'items', label: 'Artikel', suffix: '/items' },
];

export function SupplierFeedDetail() {
  const { feedId } = useParams<{ feedId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: feed } = useSupplierPriceList(feedId);
  const ingestMutation = useIngestSupplierPriceList();

  const basePath = `/i/admin/supplier-price-lists/${feedId}`;
  const activeTab: DetailTab = pathname.endsWith('/runs')
    ? 'runs'
    : pathname.endsWith('/items')
      ? 'items'
      : 'overview';

  if (!feed) {
    return (
      <div className="admin-empty">
        <div><div className="big">Laden…</div></div>
      </div>
    );
  }

  const isOrphan = !!feed.deletedAt;

  const handleIngest = () => {
    ingestMutation.mutate(feedId!);
  };

  return (
    <div className="cp-frame">
      <div className="spl-detail-bar">
        <div className="spl-detail-bar-inner">
          <div className="spl-detail-header">
            <button className="bp-cmd-back" onClick={() => navigate('/i/admin/supplier-price-lists')}>← Zurück</button>
            <span className="spl-detail-name">{feed.name}</span>
            {isOrphan && <span className="ds-pill neg">Verwaist</span>}
            {!isOrphan && (
              <span className={`ds-pill ${feed.enabled ? 'pos' : 'muted'}`}>
                {feed.enabled ? 'Aktiv' : 'Deaktiviert'}
              </span>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {!isOrphan && (
                <IfPermitted permission={PERMISSIONS.SUPPLIER_PRICE_LISTS_MANAGE}>
                  <DsButton
                    size="small"
                    disabled={ingestMutation.isPending}
                    onClick={handleIngest}
                  >
                    {ingestMutation.isPending ? 'Wird eingereiht…' : '↻ Abruf starten'}
                  </DsButton>
                </IfPermitted>
              )}
            </div>
          </div>

          {ingestMutation.isSuccess && (
            <div className="ds-alert success" style={{ marginTop: 8 }}>
              Abruf-Job eingereiht. Wechsle zum Tab „Läufe" für den Status.
            </div>
          )}

          {isOrphan && (
            <div className="ds-alert" style={{ marginTop: 8 }}>
              Dieser Feed existiert nicht mehr im Code (
              <code style={{ fontFamily: 'var(--font-mono)' }}>
                supplier-price-lists/config/feeds.config.ts
              </code>
              ) und wurde beim letzten Start deaktiviert. Historische Läufe und
              Artikel bleiben erhalten.
            </div>
          )}

          <div className="bp-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`bp-tab${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => navigate(`${basePath}${tab.suffix}`)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Outlet context={{ feed, feedId: feedId! }} />
    </div>
  );
}
